import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  useGetPlaylistByIdQuery,
  useCreatePlaylistMutation,
  useUpdatePlaylistMutation,
} from "../../services/modules/playlist";
import { useGetAllVideosQuery } from "../../services/modules/video";
import { useGetAllNetworksQuery } from "../../services/modules/network";
import { useGetAllPlaylistCategoriesQuery } from "../../services/modules/playlistCategory";
import {
  setSelectedNetworks,
  setTitle,
  setDescription,
  setCoverImage,
  setSelectedPlaylistVideos,
  addVideoToPlaylist,
  removeVideoFromPlaylist,
  resetPlaylistState,
} from "../../store/playlist";
import { setSelectedPlaylistCategory } from "../../store/playlistCategories";

import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const NetworkBadge = ({ name }) => (
  <span className="inline-block text-[11px] px-2 py-0.5 rounded-full bg-gray-100 border mr-1 mt-1">
    {name}
  </span>
);

const Pill = ({ label, onRemove }) => (
  <span className="inline-flex items-center text-xs bg-gray-200 rounded-full px-2 py-1 mr-2 mb-2">
    {label}
    <button
      onClick={onRemove}
      className="ml-1 rounded-full w-4 h-4 leading-4 text-center text-gray-600 hover:bg-gray-300"
      aria-label={`Rimuovi ${label}`}
      title="Rimuovi"
    >
      ×
    </button>
  </span>
);

const PlaylistList = () => {
  const navigate = useNavigate();
  const { id: playlistId } = useParams();
  const dispatch = useDispatch();

  const selectedNetworks = useSelector((s) => s.playlist.selectedNetworks);
  const title = useSelector((s) => s.playlist.title);
  const description = useSelector((s) => s.playlist.description);
  const coverImage = useSelector((s) => s.playlist.coverImage);
  const selectedVideos = useSelector((s) => s.playlist.selectedVideos);
  const selectedPlaylistCategory = useSelector(
    (s) => s.playlistCategories.selectedPlaylistCategory
  );

  const { data: networksData } = useGetAllNetworksQuery();
  const { data: videosData, isLoading: loadingVideos } = useGetAllVideosQuery({
    page: 1,
    pageSize: 2000,
  });
  const { data: playlistDetailData, error: playlistError } =
    useGetPlaylistByIdQuery(
      { id: playlistId },
      {
        skip: !playlistId || playlistId === "new",
        refetchOnMountOrArgChange: true,
        refetchOnReconnect: true,
      }
    );
  const { data: playlistCategoriesData } = useGetAllPlaylistCategoriesQuery();

  const [createPlaylist, { isLoading: saving }] = useCreatePlaylistMutation();
  const [updatePlaylist] = useUpdatePlaylistMutation();

  const [searchTerm, setSearchTerm] = useState("");
  const [onlySelectedNets, setOnlySelectedNets] = useState(true);

  const networks = networksData?.data?.result || [];
  const videos = videosData?.data?.result || [];
  const playlistCategories = playlistCategoriesData?.data?.result || [];

  const playlistResult = playlistDetailData?.data?.result;
  const playlistResultId = playlistResult?.id;
  const isPlaylistIdNumeric =
    typeof playlistResultId === "number"
      ? !Number.isNaN(playlistResultId)
      : typeof playlistResultId === "string"
      ? /^\d+$/.test(playlistResultId)
      : false;
  const playlistErrorStatus =
    playlistError && typeof playlistError === "object" && "status" in playlistError
      ? playlistError.status
      : null;
  const isCreateMode =
    !playlistId ||
    playlistId === "new" ||
    playlistErrorStatus === 400 ||
    (playlistResult && !isPlaylistIdNumeric);

  // Carica dati in modalità "edit"
  useEffect(() => {
    if (playlistDetailData && playlistId) {
      const p = playlistDetailData.data.result;
      const videoList = p.videos || [];

      dispatch(setTitle(p.name || ""));
      dispatch(setDescription(p.description || ""));
      dispatch(setCoverImage(p.coverImage || ""));

      const nets = (p.networks || []).map((n) => Number(n.id));
      dispatch(setSelectedNetworks(nets));

      dispatch(
        setSelectedPlaylistCategory(
          p.playlistCategoryId != null ? Number(p.playlistCategoryId) : ""
        )
      );

      dispatch(
        setSelectedPlaylistVideos(
          videoList.map((item) => ({
            id: item.video?.id,
            title: item.video?.title || "Senza titolo",
            thumbnail: item.video?.thumbnail || "https://via.placeholder.com/50x30",
          }))
        )
      );
    }
  }, [playlistDetailData, playlistId, dispatch]);

  // Resetta form se "new"
  useEffect(() => {
    if (isCreateMode) {
      dispatch(resetPlaylistState());
    }
  }, [isCreateMode, dispatch]);

  // Filtro per reti selezionate + testo
  const filteredVideos = useMemo(() => {
    const matchText = (v) =>
      `${v.title}`.toLowerCase().includes(searchTerm.toLowerCase());

    const matchNetworks = (v) => {
      if (!selectedNetworks?.length) return !onlySelectedNets; // se nessuna rete e toggle attivo, mostra nulla
      const vNetIds = (v.networks || []).map((n) => Number(n.id));
      return vNetIds.some((id) => selectedNetworks.includes(id));
    };

    return videos.filter(
      (v) => matchText(v) && (onlySelectedNets ? matchNetworks(v) : true)
    );
  }, [videos, searchTerm, selectedNetworks, onlySelectedNets]);

  // Helpers reti
  const toggleNetwork = (id) => {
    const set = new Set(selectedNetworks);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    dispatch(setSelectedNetworks(Array.from(set)));
  };
  const selectAllNetworks = () =>
    dispatch(setSelectedNetworks(networks.map((n) => Number(n.id))));
  const clearAllNetworks = () => dispatch(setSelectedNetworks([]));

  const handleSave = async () => {
    if (!selectedNetworks.length || !title || selectedVideos.length === 0) {
      toast.error("Completa tutti i campi obbligatori!");
      return;
    }

    const payload = {
      networkIds: selectedNetworks.map(Number),
      name: title, // usa "title" se il tuo backend lo richiede
      description,
      coverImage,
      playlistCategoryId: selectedPlaylistCategory
        ? Number(selectedPlaylistCategory)
        : null,
      videos: JSON.stringify(
        selectedVideos.map((v, index) => ({
          id: v.id,
          displayOrder: index + 1,
        }))
      ),
    };

    try {
      if (!isCreateMode && playlistId) {
        await updatePlaylist({ id: playlistId, ...payload }).unwrap();
        toast.success("Playlist aggiornata!");
        navigate("/admin/playlist");
      } else {
        await createPlaylist(payload).unwrap();
        toast.success("Playlist creata!");
        dispatch(resetPlaylistState());
        navigate("/admin/playlist");
      }
    } catch (err) {
      console.error("Errore salvataggio:", err);
      toast.error("Errore durante il salvataggio.");
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = selectedVideos.findIndex(
      (v) => v.id.toString() === active.id
    );
    const newIndex = selectedVideos.findIndex(
      (v) => v.id.toString() === over.id
    );
    const reordered = arrayMove(selectedVideos, oldIndex, newIndex);
    dispatch(setSelectedPlaylistVideos(reordered));
  };

  // Aggiungi tutti i filtrati
  const addAllFiltered = () => {
    let added = 0;
    filteredVideos.forEach((v) => {
      const exists = selectedVideos.some((sv) => sv.id === v.id);
      if (!exists) {
        dispatch(
          addVideoToPlaylist({
            id: v.id,
            title: v.title,
            thumbnail: v.thumbnail || "https://via.placeholder.com/50x30",
          })
        );
        added++;
      }
    });
    if (added) toast.success(`Aggiunti ${added} video`);
    else toast.info("Nessun nuovo video da aggiungere");
  };

  return (
    <div className="p-6">
      <ToastContainer position="top-center" autoClose={3000} />
      <h2 className="text-xl font-bold mb-6">
        {isCreateMode ? "Crea Playlist" : "Modifica Playlist"}
      </h2>

      <div className="mb-6 grid grid-cols-2 gap-6">
        {/* Reti (UI migliorata) */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="font-medium">Reti</label>
            <div className="text-xs text-gray-500">
              Selezionate: <b>{selectedNetworks.length}</b> / {networks.length}
            </div>
          </div>

          {/* pill selezionate */}
          {selectedNetworks.length > 0 && (
            <div className="mb-2">
              {selectedNetworks
                .map((id) => networks.find((n) => Number(n.id) === Number(id)))
                .filter(Boolean)
                .map((n) => (
                  <Pill
                    key={n.id}
                    label={n.name}
                    onRemove={() => toggleNetwork(Number(n.id))}
                  />
                ))}
            </div>
          )}

          {/* azioni rapide + toggle */}
          <div className="flex gap-2 mb-3">
            <button
              type="button"
              onClick={selectAllNetworks}
              className="text-xs px-2 py-1 border rounded hover:bg-gray-50"
            >
              Seleziona tutto
            </button>
            <button
              type="button"
              onClick={clearAllNetworks}
              className="text-xs px-2 py-1 border rounded hover:bg-gray-50"
            >
              Nessuna
            </button>

            <label className="ml-auto inline-flex items-center gap-2 text-xs cursor-pointer select-none">
              <input
                type="checkbox"
                checked={onlySelectedNets}
                onChange={(e) => setOnlySelectedNets(e.target.checked)}
                className="accent-red-600"
              />
              Mostra solo video delle reti selezionate
            </label>
          </div>

          {/* checkbox grid (rosse + highlight) */}
          <div className="grid grid-cols-2 gap-2 max-h-44 overflow-y-auto p-2 border rounded bg-white">
            {networks.map((n) => {
              const checked = selectedNetworks.includes(Number(n.id));
              return (
                <label
                  key={n.id}
                  className={`flex items-center gap-2 p-2 rounded border cursor-pointer hover:bg-gray-50 ${
                    checked ? "border-red-600 bg-red-50" : "border-gray-200"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleNetwork(Number(n.id))}
                    className="accent-red-600"
                  />
                  <span className="text-sm">{n.name}</span>
                </label>
              );
            })}
          </div>
        </div>

        {/* Categoria + campi */}
        <div>
          <label className="font-medium block mb-2">Categoria Playlist</label>
          <select
            value={selectedPlaylistCategory || ""}
            onChange={(e) =>
              dispatch(
                setSelectedPlaylistCategory(Number(e.target.value) || "")
              )
            }
            className="border p-2 rounded w-full focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
          >
            <option value="">-- Seleziona Categoria Playlist --</option>
            {playlistCategories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>

          {/* Titolo */}
          <div className="mt-4">
            <label className="font-medium block mb-2">Titolo Playlist</label>
            <input
              type="text"
              placeholder="Titolo playlist visibile all'utente"
              value={title}
              onChange={(e) => dispatch(setTitle(e.target.value))}
              className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              required
            />
          </div>

          {/* Descrizione */}
          <div className="mt-4">
            <label className="font-medium block mb-2">Descrizione</label>
            <textarea
              placeholder="Descrizione della playlist"
              value={description}
              onChange={(e) => dispatch(setDescription(e.target.value))}
              className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>

          {/* Cover */}
          <div className="mt-4">
            <label className="font-medium block mb-2">
              Immagine Copertina (URL)
            </label>
            <input
              type="text"
              placeholder="https://..."
              value={coverImage}
              onChange={(e) => dispatch(setCoverImage(e.target.value))}
              className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>
        </div>
      </div>

      {/* Search sticky + azioni lista */}
      <div className="mb-4 sticky top-0 bg-white/70 backdrop-blur z-10 py-2">
        <div className="flex items-center gap-3">
          <input
            type="text"
            placeholder="Cerca video..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border p-2 rounded w-[28%] min-w-[260px] focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
          />
          <button
            type="button"
            onClick={addAllFiltered}
            className="text-sm px-3 py-2 border rounded hover:bg-gray-50"
            title="Aggiunge tutti i video attualmente filtrati"
          >
            + Aggiungi tutti i filtrati
          </button>
          <div className="ml-auto text-sm text-gray-600">
            Mostrati: <b>{filteredVideos.length}</b> · Selezionati:{" "}
            <b>{selectedVideos.length}</b>
          </div>
        </div>
      </div>

      {/* Liste video */}
      <div className="grid grid-cols-2 gap-6">
        {/* Video Disponibili */}
        <div className="border p-4 rounded h-[500px] overflow-y-auto bg-white focus-within:ring-2 focus-within:ring-red-500">
          <h4 className="font-bold mb-2">Video Disponibili</h4>
          {loadingVideos ? (
            <p>Caricamento...</p>
          ) : filteredVideos.length === 0 ? (
            <p className="text-sm text-gray-500">
              Nessun video corrisponde ai filtri.
            </p>
          ) : (
            filteredVideos.map((video) => {
              const already = selectedVideos.some((sv) => sv.id === video.id);
              return (
                <div
                  key={video.id}
                  className="flex justify-between items-center p-2 border-b"
                >
                  <div className="flex gap-2 items-center min-w-0">
                    <img
                      src={
                        video.thumbnail || "https://via.placeholder.com/50x30"
                      }
                      alt="thumb"
                      className="w-16 h-10 object-cover rounded flex-shrink-0"
                    />
                    <div className="min-w-0">
                      {/* titolo tronco, niente a capo */}
                      <span className="block max-w-[220px] truncate">
                        {video.title}
                      </span>
                      <div className="mt-0.5">
                        {(video.networks || []).map((n) => (
                          <NetworkBadge key={n.id} name={n.name} />
                        ))}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() =>
                      !already &&
                      dispatch(
                        addVideoToPlaylist({
                          id: video.id,
                          title: video.title,
                          thumbnail:
                            video.thumbnail ||
                            "https://via.placeholder.com/50x30",
                        })
                      )
                    }
                    disabled={already}
                    className={`px-2 py-1 rounded text-sm ml-2 ${
                      already
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-red-600 hover:underline"
                    }`}
                    title={already ? "Già aggiunto" : "Aggiungi alla playlist"}
                  >
                    {already ? "Già aggiunto" : "+ Aggiungi"}
                  </button>
                </div>
              );
            })
          )}
        </div>

        {/* Video Selezionati */}
        <div className="border p-4 rounded h-[500px] overflow-y-auto bg-white">
          <h4 className="font-bold mb-2">
            Video Selezionati ({selectedVideos.length}/100)
          </h4>
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={selectedVideos.map((v) => v.id.toString())}
              strategy={verticalListSortingStrategy}
            >
              {selectedVideos.map((video) => (
                <SortableVideoItem
                  key={video.id}
                  video={video}
                  removeVideo={(id) => dispatch(removeVideoFromPlaylist(id))}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      </div>

      {/* Salva */}
      <div className="mt-6 text-center">
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-gray-800 text-white px-6 py-2 rounded hover:bg-gray-700 disabled:opacity-50"
        >
          {saving ? "Salvataggio..." : playlistId ? "Salva Modifiche" : "Crea Playlist"}
        </button>
      </div>
    </div>
  );
};

export default PlaylistList;

const SortableVideoItem = ({ video, removeVideo }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: video.id.toString() });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex justify-between items-center p-2 border-b"
    >
      <div
        className="flex gap-2 items-center cursor-grab"
        {...attributes}
        {...listeners}
      >
        <img
          src={video.thumbnail || "https://via.placeholder.com/50x30"}
          alt="thumbnail"
          className="w-16 h-10 object-cover rounded"
        />
        <span className="max-w-[220px] truncate">{video.title}</span>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          removeVideo(video.id);
        }}
        className="text-red-600 hover:underline ml-4"
      >
        Rimuovi
      </button>
    </div>
  );
};
